import { createTheme, ThemeOptions, Shadows } from '@mui/material/styles';
import luisa from './luisa';
import { colors, typography, spacings, shadows } from './tokens';

// Add custom properties to the theme
declare module '@mui/material/styles' {
    interface Palette {
        leadblue: Record<string, string>;
        devblue: Record<string, string>;
        headset: Record<string, string>;
        gray: Record<string, string>;
        green: Record<string, string>;
        yellow: Record<string, string>;
        red: Record<string, string>;
        pink: Record<string, string>;
        white: Record<string, string>;
    }

    interface PaletteOptions {
        leadblue?: Record<string, string>;
        devblue?: Record<string, string>;
        headset?: Record<string, string>;
        gray?: Record<string, string>;
        green?: Record<string, string>;
        yellow?: Record<string, string>;
        red?: Record<string, string>;
        pink?: Record<string, string>;
        white?: Record<string, string>;
    }
}

// Define the theme options
const themeOptions: ThemeOptions = {
    palette: {
        // Standard MUI palette with Luisa color swatches
        ...colors,
        // Add Luisa color swatches to the palette for direct access
        leadblue: luisa.swatches.leadblue,
        devblue: luisa.swatches.devblue,
        headset: luisa.swatches.headset,
        gray: luisa.swatches.gray,
        green: luisa.swatches.green,
        yellow: luisa.swatches.yellow,
        red: luisa.swatches.red,
        pink: luisa.swatches.pink,
        white: luisa.swatches.white,
        action: {
            hover: luisa.swatches.devblue[10],
        },
    },
    typography: {
        fontFamily: typography.fontFamily,
        h1: typography.h1,
        h2: typography.h2,
        h3: typography.h3,
        h4: typography.h4,
        h5: typography.h5,
        h6: typography.h6,
        body1: typography.body1,
        body2: typography.body2,
        subtitle1: typography.subtitle1,
        caption: typography.caption,
    },
    spacing: spacings,
    shadows: shadows as Shadows,
    components: {
        // Global CSS overrides
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: colors.background.default,
                    color: colors.text.primary,
                },
            },
        },
        // Tab customization
        MuiTab: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    padding: `${spacings(1.25)} ${spacings(1.5)}`,
                    color: colors.text.primary,
                    '&:hover': {
                        backgroundColor: luisa.swatches.devblue['10'],
                        transition: 'background-color 0.3s ease',
                    },
                    '&.Mui-selected': {
                        color: colors.primary.dark,
                    },
                },
            },
        },
        // Tabs customization
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: colors.primary.dark,
                    height: '3px',
                    transition: 'none',
                },
            },
        },
        // Button customization
        MuiButton: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                },
                contained: {
                    boxShadow: 'none',
                },
                outlined: {
                    borderWidth: '1px',
                    '&:hover': {
                        borderWidth: '1px',
                    },
                },
            },
        },
        // Card customization
        MuiCard: {
            styleOverrides: {
                root: {
                    padding: 4,
                },
            },
        },
        // TableRow customization
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: luisa.swatches.devblue[10],
                        transition: 'background-color 0.2s ease',
                    },
                },
            },
        },
        // Select dropdown customization
        MuiSelect: {
            styleOverrides: {
                root: {
                    backgroundColor: luisa.swatches.white['100'],
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: luisa.swatches.gray['30'],
                        borderWidth: '1px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: luisa.swatches.gray['60'],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: luisa.swatches.leadblue['100'],
                        borderWidth: '1px',
                    },
                },
            },
        },
        // Menu (dropdown list) customization
        MuiMenu: {
            styleOverrides: {
                paper: {
                    border: `1px solid ${luisa.swatches.gray['30']}`,
                    boxShadow: shadows[1],
                    borderRadius: 0,
                },
                list: {
                    padding: 0,
                    borderRadius: 0,
                },
            },
        },
        // MenuItem customization
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    padding: '6px 16px',
                    fontSize: typography.body1.fontSize,
                    fontFamily: typography.fontFamily,
                    '&:hover': {
                        backgroundColor: luisa.swatches.devblue['10'],
                    },
                    '&.Mui-selected': {
                        backgroundColor: luisa.swatches.devblue['10'],
                        '&:hover': {
                            backgroundColor: luisa.swatches.devblue['30'],
                        },
                    },
                },
            },
        },
        // Paper customization (affects all popup menus)
        MuiPaper: {
            styleOverrides: {
                root: {
                    '&.MuiPopover-paper': {
                        border: `1px solid ${luisa.swatches.gray['30']}`,
                        borderRadius: '4px',
                    },
                },
            },
        },
        // TextField customization
        MuiTextField: {
            defaultProps: {
                InputLabelProps: {
                    shrink: true,
                },
                variant: 'outlined',
            },
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: luisa.swatches.white['100'],
                        '& fieldset': {
                            borderColor: luisa.swatches.gray['30'],
                            borderWidth: '1px',
                            top: 0,
                            legend: {
                                display: 'none',
                            },
                        },
                        '&:hover:not(.Mui-disabled) fieldset': {
                            borderColor: luisa.swatches.gray['60'],
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: luisa.swatches.leadblue['100'],
                            borderWidth: '1px',
                        },
                    },
                },
            },
        },
        // InputLabel customization
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: typography.body2.fontSize,
                    transform: 'none',
                    position: 'static',
                    marginBottom: '4px',
                    pointerEvents: 'auto',
                    '&.Mui-focused': {
                        color: luisa.swatches.devblue['100'],
                    },
                },
                outlined: {
                    transform: 'none',
                    position: 'static',
                    marginBottom: '4px',
                    '&.MuiInputLabel-shrink': {
                        transform: 'none',
                    },
                },
            },
        },
        // TextArea and OutlinedInput customization
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: luisa.swatches.white['100'],
                    '& textarea': {
                        padding: '0',
                    },
                    '& fieldset': {
                        top: 0,
                        legend: {
                            display: 'none',
                        },
                    },
                    '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: luisa.swatches.leadblue['100'],
                            borderWidth: '1px',
                        },
                    },
                },
                notchedOutline: {
                    top: 0,
                    borderColor: luisa.swatches.gray['30'],
                    borderWidth: '1px',
                    legend: {
                        display: 'none',
                    },
                },
            },
        },
        // FormHelperText customization
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    marginRight: 0,
                    '&.Mui-error': {
                        color: luisa.swatches.red['90'],
                    },
                },
            },
        },
        // Alert customization
        MuiAlert: {
            styleOverrides: {
                root: {
                    fontSize: typography.body2.fontSize,
                    fontFamily: typography.fontFamily,
                },
            },
        },
        // TablePagination customization
        MuiTablePagination: {
            styleOverrides: {
                select: {
                    '&.MuiTablePagination-select': {
                        border: `1px solid ${luisa.swatches.gray['30']}`,
                        borderRadius: '4px',
                        fontSize: typography.body2.fontSize,
                        fontFamily: typography.fontFamily,
                        '&:hover': {
                            borderColor: luisa.swatches.gray['60'],
                        },
                        '&:focus': {
                            borderColor: luisa.swatches.leadblue['100'],
                        },
                    },
                },
                selectRoot: {
                    fontSize: typography.body2.fontSize,
                    fontFamily: typography.fontFamily,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: luisa.swatches.gray['30'],
                        borderWidth: '1px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: luisa.swatches.gray['60'],
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: luisa.swatches.leadblue['100'],
                        borderWidth: '1px',
                    },
                },
            },
        },
        // Dialog customization
        MuiDialog: {
            styleOverrides: {
                paper: {
                    padding: spacings(1),
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    padding: spacings(1),
                    paddingBottom: spacings(1),
                    fontSize: typography.h3.fontSize,
                    fontWeight: typography.h3.fontWeight,
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: spacings(1),
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: spacings(1),
                },
            },
        },
        // Tooltip customization
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    padding: spacings(0.5),
                    fontSize: '0.75rem',
                    lineHeight: spacings(1),
                },
            },
        },
        // Radio button customization
        MuiRadio: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    color: luisa.swatches.devblue['100'],
                    padding: spacings(0.75),
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                    '&.Mui-checked': {
                        color: luisa.swatches.devblue['100'],
                    },
                    '& .MuiSvgIcon-root': {
                        fontSize: '1.25rem',
                    },
                    '& svg': {
                        strokeWidth: '1px',
                    },
                },
            },
        },
    },
};

const theme = createTheme(themeOptions);

export default theme;
